import ServiceFeeNote from "@/components/ServiceFeeNote";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import PayPlayer from "@/components/PayPlayer";
import SportsCardStudio from "@/components/SportsCardStudio";
import RedemptionPortal from "@/components/RedemptionPortal";
import MerchantShopManager from "@/components/MerchantShopManager";
import AdBanner from "@/components/AdBanner";
import { CreditDisclaimer } from "@/components/CreditDisclaimer";
import { PageHeader } from "@/components/perf";
import { Coins, Send, Copy, Loader2, ArrowLeft, ShoppingBag, Snowflake, ScrollText, CreditCard } from "lucide-react";

const TYPE_META = {
  purchase: { label: "Purchased", color: "#A3E635" },
  cash_credit: { label: "Cash Purchase", color: "#A3E635" },
  spend: { label: "Spent", color: "#F59E0B" },
  pos_charge: { label: "Event Purchase", color: "#F59E0B" },
  transfer_in: { label: "Received", color: "#22D3EE" },
  transfer_out: { label: "Sent", color: "#22D3EE" },
  admin_deduct: { label: "Adjustment", color: "#EF4444" },
  bonus_credit: { label: "Bonus Coins", color: "#C084FC" },
  refund: { label: "Refund", color: "#4ADE80" },
  payment_in: { label: "Payment Received", color: "#A3E635" },
};

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [config, setConfig] = useState(null);
  const [customCoins, setCustomCoins] = useState("");
  const [buying, setBuying] = useState(false);
  const [ackAgreed, setAckAgreed] = useState(false);
  useEffect(() => { if (wallet?.disclaimer_ack_at) setAckAgreed(true); }, [wallet?.disclaimer_ack_at]);
  const [verifying, setVerifying] = useState(false);
  const [payReq, setPayReq] = useState(null);
  const [splitMode, setSplitMode] = useState(false);
  const [splitCoins, setSplitCoins] = useState("");
  const [splitBusy, setSplitBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [w, c] = await Promise.all([api.get("/bullion/wallet"), api.get("/bullion/config")]);
      setWallet(w.data); setConfig(c.data);
    } catch (e) { /* ignore */ }
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Payment status check timed out. Check back shortly."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false);
        toast.success("Payment successful — BULLION added to your wallet! 🪙");
        load();
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch { setVerifying(false); toast.error("Could not verify payment."); }
  }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
    const pid = params.get("pay");
    if (pid) {
      api.get(`/bullion/pay-requests/${pid}`).then(({ data }) => setPayReq(data))
        .catch(() => toast.error("Payment request not found"));
    }
    load();
  }, []); // eslint-disable-line

  const confirmPayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/bullion/pay-requests/${payReq.id}/confirm`);
      setPayReq({ ...payReq, status: "paid", confirmation_code: data.confirmation_code });
      toast.success(`Payment sent! Confirmation ${data.confirmation_code} 🪙`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  const closePayment = () => {
    setPayReq(null);
    setSplitMode(false);
    setSplitCoins("");
    params.delete("pay");
    setParams(params, { replace: true });
  };

  const splitCardUsd = () => {
    const c = Number(splitCoins);
    if (!payReq || !c || c <= 0 || c >= payReq.coins) return null;
    return Math.round(payReq.usd * (1 - c / payReq.coins) * 100) / 100;
  };

  const startSplit = async () => {
    const c = Number(splitCoins);
    if (!c || c <= 0 || c >= payReq.coins) return toast.error(`Enter a coin amount between 0 and ${payReq.coins} BLC`);
    setSplitBusy(true);
    try {
      const { data } = await api.post(`/bullion/pay-requests/${payReq.id}/split-checkout`,
        { coins: c, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSplitBusy(false); }
  };

  const fee = (coins) => {
    if (!config || !config.apply_fee_on_purchase) return 0;
    return Math.round((coins * config.rate_usd * config.fee_pct / 100 + config.fee_flat) * 100) / 100;
  };
  const totalUsd = (coins) => Math.round((coins * (config?.rate_usd || 1) + fee(coins)) * 100) / 100;

  const buy = async (coins) => {
    const n = Number(coins);
    if (!n || n < 1) return toast.error("Enter at least 1 BLC");
    if (!ackAgreed) return toast.error("Please read and check the Credit Disclaimer & User Acknowledgment first");
    setBuying(true);
    try {
      const { data } = await api.post("/bullion/purchase/checkout", { coins: n, origin_url: window.location.origin, ack: true });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBuying(false); }
  };

  const copyCode = () => { navigator.clipboard.writeText(wallet.code); toast.success("Wallet code copied"); };

  if (!wallet || !config) return <div className="p-8 text-sm text-zinc-500 animate-pulse">Loading wallet…</div>;

  const bonusFor = (coins) => {
    const eligible = (config.packs || []).filter((p) => coins >= p.coins && p.bonus > 0);
    return eligible.length ? eligible.sort((a, b) => b.coins - a.coins)[0].bonus : 0;
  };

  return (
    <div data-testid="wallet-page">
      {verifying && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center">
          <div className="bg-pb-surface border border-pb-cyan p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-pb-cyan animate-spin" />
            <span className="font-display font-bold uppercase tracking-widest">Verifying Payment…</span>
          </div>
        </div>
      )}
      <button data-testid="wallet-back-btn" onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <PageHeader kicker="Bullpen Economy · Closed-Loop Currency" title="BULLION COIN Wallet"
        subtitle="Buy coins, spend them anywhere in the ecosystem — store, sessions, leagues, events — and send coins to other members." />

      {wallet.frozen && (
        <div data-testid="wallet-frozen-banner" className="border border-red-500/60 bg-red-500/10 p-4 mb-6 flex items-center gap-3">
          <Snowflake className="w-5 h-5 text-red-400 shrink-0" />
          <div className="text-sm text-red-300">Your wallet is frozen. Contact the Bullpen team to resolve this.</div>
        </div>
      )}

      {payReq && (
        <div data-testid="coin-payment-slot" className="border border-pb-red bg-pb-red/5 p-6 mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Coins className="w-5 h-5 text-pb-red" />
            <span className="font-display font-black text-xl uppercase">Coin Payment</span>
          </div>
          {payReq.status === "paid" ? (
            <div className="flex flex-col items-center py-6 text-center" data-testid="coin-payment-success">
              <div className="text-pb-red font-display font-black text-2xl uppercase mb-2">Payment Complete ✅</div>
              <div className="text-sm text-zinc-300 mb-1">{payReq.name}</div>
              <div className="font-mono text-lg text-pb-cyan border border-pb-cyan/40 px-4 py-2 mt-2" data-testid="coin-confirmation-code">
                {payReq.confirmation_code}
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">Save this confirmation code — the Bullpen team has been notified.</p>
              <button data-testid="coin-payment-done" onClick={closePayment}
                className="mt-4 border border-pb-border text-zinc-300 text-xs font-bold uppercase tracking-widest px-6 py-2 hover:border-pb-red hover:text-pb-red transition-colors">
                Done
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-6 mt-3">
              <div className="bg-white p-3 shrink-0">
                <QRCodeSVG value={payReq.house_code} size={110} data-testid="house-qr" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Paying the Bullpen · {payReq.house_code}</div>
                <div className="font-semibold text-white" data-testid="coin-payment-name">{payReq.name}</div>
                <div className="font-display font-black text-4xl text-pb-red mt-1" data-testid="coin-payment-amount">
                  {Number(payReq.coins).toFixed(2)} <span className="text-sm text-zinc-500">BLC</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Your spendable balance: {((wallet.spendable ?? wallet.balance)).toFixed(2)} BLC (bonus coins are used first at 10 bonus = 1 BLC)
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button data-testid="coin-payment-send" onClick={confirmPayment}
                  disabled={paying || wallet.frozen || (wallet.spendable ?? wallet.balance) < payReq.coins}
                  className="bg-pb-red text-black font-black uppercase tracking-widest px-8 py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Payment
                </button>
                {payReq.usd > 0 && (
                  <button data-testid="coin-payment-split-btn" onClick={() => { setSplitMode(!splitMode); if (!splitCoins) setSplitCoins(String(Math.max(1, Math.min(Math.floor((wallet.spendable ?? wallet.balance)), Math.floor(payReq.coins / 2))))); }}
                    className="border border-pb-cyan/60 text-pb-cyan font-black uppercase tracking-widest px-8 py-2.5 hover:bg-pb-cyan/10 active:scale-95 transition-all flex items-center justify-center gap-2 text-xs">
                    <CreditCard className="w-3.5 h-3.5" /> Split Card + Coin
                  </button>
                )}
                {(wallet.spendable ?? wallet.balance) < payReq.coins && (
                  <span className="text-[11px] text-red-400 text-center">Not enough BLC — buy coins below{payReq.usd > 0 ? " or split with card" : ""}</span>
                )}
                <button data-testid="coin-payment-cancel" onClick={closePayment}
                  className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-red-400 transition-colors">Cancel</button>
              </div>
            </div>
          )}
          {payReq.status !== "paid" && splitMode && payReq.usd > 0 && (
            <div className="mt-4 border border-pb-cyan/40 bg-pb-cyan/5 p-4" data-testid="split-payment-panel">
              <div className="text-[10px] uppercase tracking-[0.2em] text-pb-cyan font-bold mb-2">Split Payment — Coins + Card</div>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">BLC to use (max {Math.min((wallet.spendable ?? wallet.balance), payReq.coins - 0.01).toFixed(2)})</label>
                  <input data-testid="split-coins-input" type="number" min="0.01" step="0.01" max={payReq.coins - 0.01}
                    value={splitCoins} onChange={(e) => setSplitCoins(e.target.value)}
                    className="w-36 bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-cyan" />
                </div>
                <div className="text-sm text-zinc-300 pb-2" data-testid="split-summary">
                  {splitCardUsd() != null
                    ? <>You pay <b className="text-pb-red">{Number(splitCoins).toFixed(2)} BLC</b> + <b className="text-pb-cyan">${splitCardUsd().toFixed(2)}</b> by card</>
                    : <span className="text-zinc-500">Enter a coin amount below {payReq.coins} BLC</span>}
                </div>
                <button data-testid="split-continue-btn" onClick={startSplit}
                  disabled={splitBusy || splitCardUsd() == null || Number(splitCoins) > (wallet.spendable ?? wallet.balance)}
                  className="bg-pb-cyan text-black font-black uppercase tracking-widest px-6 py-2.5 hover:opacity-90 active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2 text-xs">
                  {splitBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Continue to Card
                </button>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2">Your coins are deducted once the card payment succeeds. Card portion must be at least $0.50.</p>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Balance + QR */}
        <div className="bg-pb-surface border border-pb-border p-6 flex flex-col items-center text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Your Balance</div>
          <div className="flex items-center gap-2 mb-1">
            <Coins className="w-8 h-8 text-pb-red" />
            <span data-testid="wallet-balance" className="font-display font-black text-5xl text-pb-red">{wallet.balance.toFixed(2)}</span>
          </div>
          <div className="text-xs text-zinc-500 mb-2">BULLION COIN · 1 BLC = ${config.rate_usd.toFixed(2)}</div>
          <div data-testid="wallet-bonus-balance" className="flex flex-col items-center border border-purple-400/40 bg-purple-400/10 px-3 py-1.5 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-purple-300">Bonus Coins</span>
              <span className="font-display font-black text-lg text-purple-300">{Number(wallet.bonus_balance || 0).toFixed(2)}</span>
            </div>
            <span className="text-[9px] text-purple-300/70" data-testid="wallet-bonus-rate">10 bonus = 1 BLC · worth {(Number(wallet.bonus_balance || 0) / 10).toFixed(2)} BLC</span>
          </div>
          <div className="bg-white p-3 mb-3">
            <QRCodeSVG value={wallet.code} size={120} data-testid="wallet-qr" />
          </div>
          <button data-testid="wallet-code" onClick={copyCode}
            className="flex items-center gap-2 font-mono text-pb-cyan text-sm border border-pb-border px-3 py-1.5 hover:border-pb-cyan transition-colors">
            {wallet.code} <Copy className="w-3.5 h-3.5" />
          </button>
          <p className="text-[11px] text-zinc-600 mt-3">Show this QR / code at events to pay with BULLION.</p>
        </div>

        {/* Buy coins */}
        <div className="bg-pb-surface border border-pb-border p-6 lg:col-span-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-pb-red" />
              <span className="font-display font-bold text-lg uppercase">Buy BULLION</span>
            </div>
            <button data-testid="credit-policy-link" onClick={() => navigate("/credit-policy")}
              title="Read the Platform Credit Policy"
              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-pb-cyan hover:text-white transition-colors underline underline-offset-2">
              <ScrollText className="w-3.5 h-3.5" /> Policy
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-4">Pay by card here, or pay cash at any event and staff will load your wallet on the spot.</p>
          <CreditDisclaimer agreed={ackAgreed} onChange={setAckAgreed} ackDate={wallet.disclaimer_ack_at} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {(config.packs || []).map((p) => (
              <button key={p.coins} data-testid={`buy-pack-${p.coins}`} onClick={() => buy(p.coins)} disabled={buying || wallet.frozen || !ackAgreed}
                className="border border-pb-border hover:border-pb-red p-3 text-left transition-colors disabled:opacity-40 group">
                <div className="font-display font-black text-2xl text-pb-red">{p.coins}<span className="text-xs text-zinc-500 ml-1">BLC</span></div>
                {p.bonus > 0 && <div className="text-[11px] text-pb-cyan font-bold">+{p.bonus} bonus 🎁</div>}
                <div className="text-[11px] text-zinc-500 mt-1">${totalUsd(p.coins).toFixed(2)} total</div>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Custom amount (BLC)</label>
              <input data-testid="buy-custom-input" type="number" min={1} value={customCoins}
                onChange={(e) => setCustomCoins(e.target.value)} placeholder="e.g. 75"
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
            </div>
            <button data-testid="buy-custom-btn" onClick={() => buy(customCoins)} disabled={buying || wallet.frozen || !Number(customCoins) || !ackAgreed}
              className="bg-pb-red text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
              {buying ? "Redirecting…" : "Buy"}            </button>
          </div>
          <ServiceFeeNote className="mt-2" />
          {Number(customCoins) >= 1 && (
            <div data-testid="buy-fee-breakdown" className="mt-3 text-xs text-zinc-500 font-mono">
              {Number(customCoins)} BLC = ${(Number(customCoins) * config.rate_usd).toFixed(2)}
              {fee(Number(customCoins)) > 0 && <> + ${fee(Number(customCoins)).toFixed(2)} service fee</>}
              {" "}→ <span className="text-white">${totalUsd(Number(customCoins)).toFixed(2)} total</span>
              {bonusFor(Number(customCoins)) > 0 && <span className="text-pb-cyan"> · +{bonusFor(Number(customCoins))} bonus coins</span>}
            </div>
          )}
        </div>
      </div>

      {/* Pay a Player (send / request / scan QR) */}
      <div className="mb-8">
        <PayPlayer onChange={load} frozen={wallet.frozen} transfersEnabled={config.transfers_enabled} />
      </div>

      {/* History */}
      <div className="bg-pb-surface border border-pb-border">
        <div className="p-5 border-b border-pb-border font-display font-bold text-lg uppercase">Transaction History</div>
        {wallet.transactions.length === 0 ? (
          <div className="p-5 text-sm text-zinc-600">No transactions yet — buy your first coins above!</div>
        ) : (
          <div className="divide-y divide-pb-border/40 max-h-[420px] overflow-y-auto">
            {wallet.transactions.map((t, i) => {
              const meta = TYPE_META[t.type] || { label: t.type, color: "#A1A1AA" };
              return (
                <div key={i} className="flex items-center justify-between px-5 py-3 text-sm" data-testid="wallet-txn-row">
                  <div>
                    <span className="font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                    <span className="text-zinc-500 text-xs ml-2">
                      {t.counterparty_name ? `${t.type === "transfer_in" ? "from" : "to"} ${t.counterparty_name}` : t.note}
                    </span>
                    <div className="text-[10px] text-zinc-600">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</div>
                  </div>
                  <div className="text-right">
                    <span className={`font-mono font-bold ${t.coins >= 0 ? "text-pb-red" : "text-zinc-300"}`}>
                      {t.coins >= 0 ? "+" : ""}{Number(t.coins).toFixed(2)} BLC
                    </span>
                    {t.fee_coins > 0 && <div className="text-[10px] text-zinc-600">incl. {Number(t.fee_coins).toFixed(2)} fee</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-8">
        <AdBanner zone="wallet" />
        <MerchantShopManager />
        <RedemptionPortal balance={wallet?.balance} onRedeemed={load} />
        <SportsCardStudio />
      </div>
    </div>
  );
}
